import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { num: "01", label: "Services", href: "#services" },
  { num: "02", label: "Selected Work", href: "#work" },
  { num: "03", label: "Our Approach", href: "#approach" },
  { num: "04", label: "FAQ", href: "#faq" },
];

export const LogoMark = ({ dark = true, className = "" }) => (
  <a href="#top" data-testid="logo-link" className={`flex items-center ${className}`}>
    {dark ? (
      <img src="/elevora-logo.png" alt="Elevora — Growth Through Strategy" className="h-9 w-auto" />
    ) : (
      <span className="bg-[#F2EDE1] rounded-md px-3 py-2 inline-flex">
        <img src="/elevora-logo.png" alt="Elevora — Growth Through Strategy" className="h-7 w-auto" />
      </span>
    )}
  </a>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 bg-[#F2EDE1] transition-shadow duration-500 ${
        scrolled ? "shadow-[0_1px_0_rgba(11,61,46,0.12)]" : ""
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-24 flex items-center justify-between">
        <LogoMark />
        <nav className="hidden lg:flex items-center gap-12" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}-link`}
              className="eyebrow text-[#0B3D2E] hover:text-[#A6874E] transition-colors duration-300"
            >
              <span className="text-[#A6874E] mr-2">{l.num}</span>
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-4">
          <a
            href="#contact"
            data-testid="nav-contact-cta"
            className="group hidden sm:flex items-center gap-3 bg-[#0B3D2E] text-[#F2EDE1] px-7 py-4 eyebrow hover:bg-[#A6874E] transition-colors duration-300"
          >
            Start a Conversation
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            className="lg:hidden text-[#0B3D2E]"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#F2EDE1] border-t border-[#0B3D2E]/10"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}-link`}
                  className="eyebrow text-[#0B3D2E]"
                >
                  <span className="text-[#A6874E] mr-2">{l.num}</span>
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-contact-cta"
                className="eyebrow text-[#A6874E]"
              >
                Start a Conversation
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
